import React, { useEffect } from "react";
import { Form } from "react-bootstrap";
import { FieldValues, useFormContext, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import { store } from "../../app/store";
import {
  assistantSelectors,
  handleSetBasicAdditionalInfos,
  handleSetExtraAdditionalInfos,
  wizardState
} from "./botSlice";
import { ConnectForm } from "./BotWizard";
import { AIKittyType } from "./types";

type KeyIndexed =
  | "company_name"
  | "agent_name"
  | "domain"
  | "personality"
  | "legal_sector"
  | "business_sector";

const AiAdditionalInfo = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { watch, setValue } = useFormContext();

  const wizard = useAppSelector(wizardState);

  const targetAssistantData = assistantSelectors.selectById(
    store.getState(),
    (wizard.bot_config as AIKittyType).assistant
  );

  const labelMapper: Record<string, string> = {
    company_name: t("Wizard/validationLabels/key8"),
    agent_name: t(
      wizard.category == "clone"
        ? "Wizard/validationLabels/key14"
        : "Wizard/validationLabels/key9"
    ),
    domain: t("Wizard/validationLabels/key10"),
    personality: t("Wizard/validationLabels/key11"),
    legal_sector: t("Wizard/validationLabels/key12"),
    business_sector: t("Wizard/validationLabels/key13")
  };

  const capitalizeAndRemoveUnderscore = (str: string) => {
    try {
      return str
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    } catch (error) {
      return str;
    }
  };

  const updateBasicInfos = () => {
    dispatch(
      handleSetBasicAdditionalInfos({
        additional_instructions: watch("additional_instructions"),
        welcoming: watch("welcoming")
      })
    );
  };

  const updateExtraInfos = () => {
    dispatch(
      handleSetExtraAdditionalInfos({
        agent_name: watch("agent_name"),
        company_name: watch("company_name"),
        domain: watch("domain"),
        personality: watch("personality"),
        domains: watch("domains"),
        legal_sector: watch("legal_sector"),
        business_sector: watch("business_sector")
      })
    );
  };

  useEffect(() => {
    if (wizard.bot_config && (wizard.bot_config as AIKittyType).instructions) {
      (wizard.bot_config as AIKittyType).instructions.agent_name ??
        setValue(
          "agent_name",
          (wizard.bot_config as AIKittyType).instructions.agent_name
        );

      (wizard.bot_config as AIKittyType).instructions.company_name ??
        setValue(
          "company_name",
          (wizard.bot_config as AIKittyType).instructions.company_name
        );

      (wizard.bot_config as AIKittyType).instructions.domain ??
        setValue(
          "domain",
          (wizard.bot_config as AIKittyType).instructions.domain
        );

      (wizard.bot_config as AIKittyType).instructions.personality ??
        setValue(
          "personality",
          (wizard.bot_config as AIKittyType).instructions.personality
        );
    }
  }, [wizard.bot_config]);

  const getErrorMessage = (errors: any, name: string) => {
    if (errors[name]?.type == "max") {
      return t("Wizard/validationLabels/key16");
    }

    if (wizard.category === "clone" && name === "agent_name") {
      return t("Wizard/validationLabels/key15", {
        variable: t("Wizard/validationLabels/key14")
      });
    }
    return t("Wizard/validationLabels/key15", {
      variable: labelMapper[name]
    });
  };

  return (
    <ConnectForm>
      {({
        register,
        formState: { errors }
      }: UseFormReturn<FieldValues, any>) => (
        <div className="md:tw-px-8 tw-px-1 tw-flex-col tw-justify-start tw-tw-items-start tw-inline-flex md:tw-w-2/3 tw-w-full">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-8">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
              <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t("Wizard/AiAdditionalInfo/key1")}
              </div>
              <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                {t("Wizard/AiAdditionalInfo/key2")}
                {wizard.category === "widget" &&
                  t("Wizard/AiAdditionalInfo/key3")}
              </div>
            </div>
          </div>

          <Form.Group className="tw-mb-3">
            <Form.Label>{t("Wizard/AiAdditionalInfo/key5")}</Form.Label>
            <Form.Control
              as={"textarea"}
              maxLength={500}
              placeholder={t("Wizard/AiAdditionalInfo/key6").toString()}
              defaultValue={(wizard.bot_config as AIKittyType).welcoming ?? ""}
              {...register("welcoming", {
                onChange: updateBasicInfos
              })}
            />
          </Form.Group>

          <Form.Group className="tw-mb-3">
            <Form.Label>{t("Wizard/AiAdditionalInfo/key4")}</Form.Label>
            <Form.Control
              placeholder={t("KnowledgeBasesCreateAssistantBot/ph-01")}
              type="text"
              as={"textarea"}
              maxLength={10000}
              rows={12}
              defaultValue={
                (
                  wizard.bot_config as AIKittyType
                ).additional_instructions.join() ?? ""
              }
              {...register("additional_instructions", {
                onChange: updateBasicInfos
              })}
            />
          </Form.Group>

          {targetAssistantData?.required_instructions.map((ri) => (
            <Form.Group key={ri.name} className="tw-mb-3">
              <Form.Label>
                {capitalizeAndRemoveUnderscore(labelMapper[ri.name])} *
              </Form.Label>
              <Form.Control
                as={ri.name === "personality" ? "textarea" : undefined}
                rows={ri.name === "personality" ? 5 : undefined}
                type="text"
                placeholder={ri.description}
                isInvalid={!!errors[ri.name]}
                defaultValue={
                  (wizard.bot_config as AIKittyType).instructions[
                    ri.name as KeyIndexed
                  ] ?? ""
                }
                {...register(`${ri.name}`, {
                  onChange: updateExtraInfos
                })}
              />
              <Form.Control.Feedback type="invalid">
                {getErrorMessage(errors, ri.name) as string}
              </Form.Control.Feedback>
            </Form.Group>
          ))}
        </div>
      )}
    </ConnectForm>
  );
};

export default AiAdditionalInfo;
